import { type InfiniteData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type { LeagueMatchListResponse, LeagueMatchStatus } from "../../../types";
import { queryKeys } from "../../query-keys";

const MATCHES_PAGE_SIZE = 30;

type LeagueMatchFilters = {
	status?: LeagueMatchStatus;
	seasonId?: string;
};

// Newest first, one keyset page at a time (`next_before` -> next `before`).
export function useLeagueMatches(leagueId: string | null, filters?: LeagueMatchFilters) {
	const query = useInfiniteQuery<
		LeagueMatchListResponse,
		Error,
		InfiniteData<LeagueMatchListResponse>,
		ReturnType<typeof queryKeys.leagues.matches>,
		string | undefined
	>({
		enabled: !!leagueId,
		getNextPageParam: (lastPage) => lastPage.next_before ?? undefined,
		initialPageParam: undefined,
		queryFn: ({ pageParam }) =>
			clubApi.leagues.matches.list(leagueId as string, {
				before: pageParam,
				limit: MATCHES_PAGE_SIZE,
				seasonId: filters?.seasonId,
				status: filters?.status,
			}),
		queryKey: queryKeys.leagues.matches(leagueId ?? "", filters),
	});
	return { ...query, matches: (query.data?.pages ?? []).flatMap((page) => page.items) };
}

export function useLeagueMatch(leagueId: string | null, matchId: string | null) {
	return useQuery({
		enabled: !!leagueId && !!matchId,
		queryFn: () => clubApi.leagues.matches.get(leagueId as string, matchId as string),
		queryKey: queryKeys.leagues.match(leagueId ?? "", matchId ?? ""),
	});
}

// Staff only; loaded lazily (pass `enabled: false` until the history section opens).
export function useLeagueMatchRevisions(
	leagueId: string | null,
	matchId: string | null,
	enabled = true,
) {
	return useQuery({
		enabled: !!leagueId && !!matchId && enabled,
		queryFn: () => clubApi.leagues.matches.revisions.list(leagueId as string, matchId as string),
		queryKey: queryKeys.leagues.revisions(leagueId ?? "", matchId ?? ""),
	});
}
